import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronUp, X } from "lucide-react";
import { useApi } from "../hooks/useApi";

const fmtValue = (v) => {
  if (v === null || v === undefined) return "—";
  if (typeof v !== "number" || !Number.isFinite(v)) return String(v);
  return Number.isInteger(v) ? String(v) : v.toFixed(2);
};

// Signed difference a - b (for the comparison Δ columns).
const fmtDiff = (a, b) => {
  if (typeof a !== "number" || typeof b !== "number") return "";
  const d = a - b;
  if (d === 0) return "0";
  return d > 0 ? `+${fmtValue(d)}` : fmtValue(d);
};

// Dashboard-style quarter label for a period, split into the "q1h1" part and the
// "-1" index part, e.g. "00:00 - 00:15" -> { main: "q1h1", idx: "-1" }.
const qhParts = (period) => {
  const start = (period || "").split("-")[0].trim();
  const [hh, mm] = start.split(":").map(Number);
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return { main: "", idx: "" };
  const minutes = hh * 60 + mm;
  const hour = Math.floor(minutes / 60) + 1;
  const quarter = Math.floor((minutes % 60) / 15) + 1;
  return { main: `q${quarter}h${hour}`, idx: `-${(hour - 1) * 4 + quarter}` };
};

// Full-screen popup. One pack -> a scrollable list of fields, each with a header
// and every quarter's value row by row. Multiple packs -> the same layout with a
// value column per version (#1, #2, …) plus a Δ (#N − #1) diff column.
const PackDetailModal = ({ packs, onClose }) => {
  const { fetchGet } = useApi();
  const [details, setDetails] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const isCompare = packs.length > 1;
  // Diff columns: each version (from last to #2) minus #1 — e.g. for 3 versions
  // Δ(#3−#1), Δ(#2−#1). Holds the 0-based version indices to subtract #1 from.
  const diffIdx = isCompare
    ? Array.from({ length: packs.length - 1 }, (_, k) => packs.length - 1 - k)
    : [];
  const sectionRefs = useRef({}); // field col -> section element, for jump-to
  const [indexOpen, setIndexOpen] = useState(true);

  const scrollToField = (col) => {
    // Jump directly (no smooth animation) to the field's data.
    sectionRefs.current[col]?.scrollIntoView({ block: "start" });
    setIndexOpen(false); // auto-fold the index after jumping
  };

  // Header row, reused at the top and repeated mid-table (before 08:00 / 16:00).
  const renderHeaderRow = (rowKey) => (
    <tr key={rowKey} className="bg-gray-100 text-gray-600">
      <th className="text-left px-2 py-1 border border-gray-200">Period</th>
      <th className="text-left px-2 py-1 border border-gray-200">Quarter</th>
      {packs.map((p, i) => (
        <th key={i} className="text-right px-2 py-1 border border-gray-200">
          {isCompare ? `#${i + 1}` : "Value"}
        </th>
      ))}
      {diffIdx.map((i) => (
        <th key={`d${i}`} className="text-right px-2 py-1 border border-gray-200">
          Δ (#{i + 1}−#1)
        </th>
      ))}
    </tr>
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const results = await Promise.all(
        packs.map(async (p) => {
          const params = new URLSearchParams({
            type: p.type,
            date: p.business_date,
            pub: p.publication_ts_utc,
          });
          const res = await fetchGet(`/pse/pack?${params.toString()}`);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.json();
        })
      );
      setDetails(results);
      setError(null);
    } catch (e) {
      setError(e.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [fetchGet, packs]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const periods = details?.[0]?.periods || [];
  const fields = details?.[0]?.fields || [];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header / legend */}
        <div className="flex items-start justify-between gap-3 px-5 py-3 border-b border-gray-200">
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-gray-800">
              {isCompare ? "Compare packs" : `${packs[0].type} — delivery ${packs[0].business_date}`}
            </h3>
            <div className="text-xs text-gray-500 flex flex-wrap gap-x-4">
              {packs.map((p, i) => (
                <span key={i}>
                  {isCompare ? <b className="text-gray-700">#{i + 1} </b> : null}
                  {p.type} · {p.created_at || p.publication_ts_utc}
                </span>
              ))}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded text-gray-500 hover:bg-gray-100 shrink-0"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Field index — click to jump to a section (auto-folds on click) */}
        {!loading && !error && fields.length ? (
          <div className="border-b border-gray-200 bg-gray-50">
            <button
              type="button"
              onClick={() => setIndexOpen((o) => !o)}
              className="w-full flex items-center justify-between px-4 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100"
            >
              <span>Fields ({fields.length})</span>
              {indexOpen ? (
                <ChevronUp className="w-4 h-4" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )}
            </button>
            {indexOpen ? (
              <div className="px-4 pb-2 grid grid-cols-2 md:grid-cols-3 gap-x-6">
                {fields.map((f) => (
                  <button
                    key={f.col}
                    type="button"
                    onClick={() => scrollToField(f.col)}
                    className="text-left py-0.5 hover:bg-gray-100 rounded px-1"
                  >
                    <span className="pl-label text-xs text-blue-700">
                      {f.pl}{" "}
                      <span className="font-mono text-gray-400">{f.col}</span>
                    </span>
                    <span className="block text-[10px] text-gray-400 truncate">
                      {f.en}
                    </span>
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}

        {/* Body — one scrollable page */}
        <div className="overflow-auto p-4 space-y-5">
          {loading ? (
            <div className="text-sm text-gray-500 p-4">Loading…</div>
          ) : error ? (
            <div className="text-sm text-red-600 p-4">Error: {error}</div>
          ) : (
            fields.map((f, fi) => (
              <div
                key={f.col}
                ref={(el) => {
                  sectionRefs.current[f.col] = el;
                }}
                className="scroll-mt-2"
              >
                {/* Field header */}
                <div className="mb-1 sticky top-0 bg-white py-1">
                  <div className="pl-label text-sm font-semibold text-gray-800">
                    {f.pl}{" "}
                    <span className="font-mono text-xs text-gray-400">{f.col}</span>
                  </div>
                  <div className="text-xs text-gray-500">{f.en}</div>
                </div>
                <table className="w-full text-xs border-collapse">
                  <thead>{renderHeaderRow("head")}</thead>
                  <tbody>
                    {periods.map((per, pi) => {
                      const vals = details.map((d) => d.fields[fi].values[pi]);
                      const start = per.split("-")[0].trim();
                      const repeatHeader = start === "08:00" || start === "16:00";
                      const dataRow = (
                        <tr key={pi} className="odd:bg-white even:bg-gray-50">
                          <td className="px-2 py-0.5 border border-gray-200 text-gray-600">
                            {per}
                          </td>
                          <td className="px-2 py-0.5 border border-gray-200 font-mono text-gray-900">
                            {qhParts(per).main}
                            <span className="text-red-600">{qhParts(per).idx}</span>
                          </td>
                          {vals.map((v, vi) => (
                            <td
                              key={vi}
                              className="px-2 py-0.5 border border-gray-200 text-right tabular-nums"
                            >
                              {fmtValue(v)}
                            </td>
                          ))}
                          {diffIdx.map((i) => (
                            <td
                              key={`d${i}`}
                              className="px-2 py-0.5 border border-gray-200 text-right tabular-nums font-semibold text-blue-700"
                            >
                              {fmtDiff(vals[i], vals[0])}
                            </td>
                          ))}
                        </tr>
                      );
                      return repeatHeader
                        ? [renderHeaderRow(`head-${pi}`), dataRow]
                        : dataRow;
                    })}
                  </tbody>
                </table>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default PackDetailModal;
